import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Container from '../components/layout/Container';
import DivisionStandings from '../components/standings/DivisionStandings';
import Loading from '../components/common/Loading';
import ErrorMessage from '../components/common/ErrorMessage';
import { useAuth } from '../hooks/useAuth';
import { getCompetitionDetails, getCompetitions } from '../services/public-api';
import './StandingsPage.css';

/**
 * StandingsPage
 * Shows division standings for a competition or all competitions
 * T073: Standings page showing division standings
 */
const StandingsPage = () => {
  const { competitionId } = useParams();
  const { isOrganizer, isAuthenticated } = useAuth();
  const [competition, setCompetition] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (competitionId) {
      fetchCompetition();
    } else {
      fetchAllCompetitions();
    }
  }, [competitionId]);

  const fetchCompetition = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getCompetitionDetails(competitionId);
      setCompetition(data.competition);
    } catch (err) {
      setError(err.message || 'Failed to load competition');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCompetitions = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getCompetitions();
      const activeComps = data.competitions.filter(c => c.status === 'active');
      setCompetitions(activeComps);
    } catch (err) {
      setError(err.message || 'Failed to load competitions');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Loading size="large" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage
          message={error}
          variant="error"
          onRetry={competitionId ? fetchCompetition : fetchAllCompetitions}
        />
      </Container>
    );
  }

  // Show all competitions standings
  if (!competitionId) {
    if (competitions.length === 0) {
      return (
        <Container>
          <div className="standings-page">
            <div className="page-header">
              <h1>Standings</h1>
            </div>
            <div className="empty-state">
              <div className="empty-state-icon">📊</div>
              <h2>No Active Competitions</h2>
              <p>There are no active competitions with standings to display.</p>
              {isOrganizer ? (
                <>
                  <p>Create a competition to start tracking standings.</p>
                  <Link to="/organizer/competitions/create" className="btn btn-primary">Create Competition</Link>
                </>
              ) : (
                <Link to="/competitions" className="btn btn-secondary">View All Competitions</Link>
              )}
            </div>
          </div>
        </Container>
      );
    }

    return (
      <Container>
        <div className="standings-page">
          <div className="page-header">
            <h1>Competition Standings</h1>
            <p className="page-description">View standings for all active competitions</p>
          </div>
          <div className="competitions-standings-list">
            {competitions.map((comp) => (
              <div key={comp.competitionId} className="competition-standings-section">
                <div className="competition-standings-header">
                  <h2>{comp.name}</h2>
                  <Link 
                    to={`/competitions/${comp.competitionId}/standings`}
                    className="view-full-link"
                  >
                    View Full Standings 
                  </Link>
                </div>
                {comp.divisions && comp.divisions.length > 0 ? (
                  <DivisionStandings
                    competitionId={comp.competitionId}
                    divisions={comp.divisions.map((div) => ({
                      divisionId: typeof div === 'string' ? div : div.divisionId || div,
                      name: typeof div === 'string' ? div : div.name || div,
                    }))}
                  />
                ) : (
                  <p className="no-divisions-text">No divisions configured</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </Container>
    );
  }

  // Show single competition standings
  if (!competition) {
    return (
      <Container>
        <div className="not-found">
          <h1>Competition Not Found</h1>
          <p>The competition you're looking for doesn not exist.</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="standings-page">
        <div className="page-header">
          <Link to={`/competitions/${competitionId}`} className="back-link">
             Back to Competition
          </Link>
          <h1>{competition.name} - Standings</h1>
        </div>

        {competition.divisions && competition.divisions.length > 0 ? (
          <DivisionStandings
            competitionId={competitionId}
            divisions={competition.divisions}
          />
        ) : (
          <div className="no-divisions">
            <p>No divisions configured for this competition.</p>
          </div>
        )}
      </div>
    </Container>
  );
};

export default StandingsPage;